import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ExportMenu } from "../../components/reports/ExportMenu";
import { FilterBar } from "../../components/ui/FilterBar";
import { Input } from "../../components/ui/Input";
import { PageHeader } from "../../components/ui/PageHeader";
import { ReportSummaryCards } from "../../components/ui/ReportSummaryCards";
import { Select } from "../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import { type DeviceHealthRow, reportsApi } from "../../lib/api/reports";
import { formatDateTime } from "../../lib/format";
import { t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

export function DeviceHealthReportPage() {
  useLocale();
  const [platform, setPlatform] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const query = useQuery({
    queryKey: ["report-device-health", platform, status, search],
    queryFn: () =>
      reportsApi.getDeviceHealth({
        platform: platform || undefined,
        status: status || undefined,
        search: search || undefined,
      }),
  });

  const filters = {
    platform: platform || undefined,
    status: status || undefined,
    search: search || undefined,
  };

  const columns: Column<DeviceHealthRow>[] = [
    {
      key: "device",
      header: t("reports.deviceHealth.column.device"),
      render: (row) => (
        <Link to={`/devices/${row.device_id}`}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{row.device_uid}</span>
        </Link>
      ),
    },
    {
      key: "user",
      header: t("reports.deviceHealth.column.user"),
      render: (row) => row.user_full_name || row.user_email,
    },
    {
      key: "platform",
      header: t("common.platform.android"),
      render: (row) => <Badge tone="info">{t(`common.platform.${row.platform}`, row.platform)}</Badge>,
    },
    {
      key: "status",
      header: t("common.status"),
      render: (row) => <Badge tone="neutral">{t(`devices.statusValue.${row.status}`, row.status)}</Badge>,
    },
    {
      key: "version",
      header: t("reports.deviceHealth.column.appVersion"),
      render: (row) => row.app_version || "—",
    },
    {
      key: "policy",
      header: t("reports.deviceHealth.column.policyVersion"),
      render: (row) => (row.current_policy_version != null ? `v${row.current_policy_version}` : "—"),
      align: "right",
    },
    {
      key: "ack",
      header: t("reports.deviceHealth.column.lastAck"),
      render: (row) => formatDateTime(row.last_policy_ack_at),
    },
    {
      key: "seen",
      header: t("reports.deviceHealth.column.lastSeen"),
      render: (row) => formatDateTime(row.last_seen_at),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t("reports.deviceHealth.title")}
        subtitle={t("reports.deviceHealth.subtitle")}
        actions={
          <ExportMenu
            reportName="device-health"
            filenameStem="device-health"
            filters={filters}
          />
        }
      />

      <div className="stack">
        {query.data && (
          <ReportSummaryCards
            metrics={[
              { labelKey: "reports.deviceHealth.summary.total", value: query.data.summary.total_devices },
              { labelKey: "reports.deviceHealth.summary.active", value: query.data.summary.active_devices },
              { labelKey: "reports.deviceHealth.summary.stale", value: query.data.summary.stale_devices },
              { labelKey: "reports.deviceHealth.summary.neverSeen", value: query.data.summary.never_seen_devices },
              { labelKey: "reports.deviceHealth.summary.ackMissing", value: query.data.summary.latest_policy_ack_missing },
            ]}
          />
        )}

        <FilterBar>
          <span className="filter-bar__label">{t("devices.filter.platform")}</span>
          <Select value={platform} onChange={(e) => setPlatform(e.target.value)}>
            <option value="">{t("common.all")}</option>
            <option value="android">{t("common.platform.android")}</option>
            <option value="ios">{t("common.platform.ios")}</option>
          </Select>
          <span className="filter-bar__label">{t("devices.filter.status")}</span>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">{t("common.all")}</option>
            <option value="online">{t("devices.statusValue.online")}</option>
            <option value="recent">{t("devices.statusValue.recent")}</option>
            <option value="stale">{t("devices.statusValue.stale")}</option>
          </Select>
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("devices.filter.searchPlaceholder")}
            style={{ width: 240 }}
          />
        </FilterBar>

        <Card padding="none">
          {query.isLoading && <LoadingState />}
          {query.error && (
            <ErrorState
              titleKey="states.errorTitle"
              message={translateError(query.error)}
              action={
                <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                  {t("common.retry")}
                </Button>
              }
            />
          )}
          {query.data && (
            <Table
              rows={query.data.rows}
              columns={columns}
              rowKey={(row) => row.device_id}
              empty={
                <EmptyState
                  titleKey="reports.deviceHealth.emptyTitle"
                  bodyKey="reports.deviceHealth.emptyBody"
                />
              }
            />
          )}
        </Card>
      </div>
    </div>
  );
}
